import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { BookCardItem, type BookCard } from "@/components/BookCard";
import { getStoredLocation, type Coords } from "@/lib/location";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

type Shelf = { id: string; name: string; emoji: string | null; description: string | null; user_id: string };

export const Route = createFileRoute("/shelves/$id")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase.from("shelves").select("name, emoji, description").eq("id", params.id).maybeSingle();
      return { shelf: data ?? null };
    } catch {
      return { shelf: null };
    }
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.shelf as { name: string; emoji: string | null; description: string | null } | null | undefined;
    const title = s ? `${s.emoji ? `${s.emoji} ` : ""}${s.name} — shelf on Book Swap` : "Shelf — Book Swap";
    const description = (
      s?.description?.trim() ||
      (s ? `Books on the "${s.name}" shelf, shared by a Book Swap member for neighbors to borrow, swap or receive.` : "A curated shelf of books shared by a Book Swap member with neighbors nearby.")
    ).slice(0, 160);
    return {
      ...pageHead({ path: `/shelves/${params.id}`, title, description }),
      scripts: [
        jsonLd({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: s?.name ? `${s.name} shelf` : "Shelf on Book Swap",
          description,
          url: `${SITE_URL}/shelves/${params.id}`,
          isPartOf: { "@type": "WebSite", name: "Book Swap", url: SITE_URL },
          about: { "@type": "Thing", name: "A curated shelf of books shared with neighbours" },
        }),
      ],
    };
  },
  component: ShelfDetail,
});

function ShelfDetail() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [books, setBooks] = useState<BookCard[]>([]);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [myBooks, setMyBooks] = useState<Array<{ id: string; title: string; author: string }>>([]);
  const [open, setOpen] = useState(false);
  const isOwner = !!user && shelf?.user_id === user.id;

  async function load() {
    setCoords(getStoredLocation());
    const { data: s } = await supabase.from("shelves").select("*").eq("id", id).maybeSingle();
    setShelf(s as Shelf | null);
    const { data: links } = await supabase.from("shelf_books").select("book_id").eq("shelf_id", id);
    const bookIds = (links ?? []).map((l: any) => l.book_id);
    if (bookIds.length === 0) { setBooks([]); return; }
    const { data: bs } = await supabase.from("books").select("id,title,author,cover_url,category,age_group,exchange_duration,condition,lat,lng,status").in("id", bookIds);
    setBooks((bs as BookCard[]) ?? []);
  }
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!isOwner || !user) return;
    supabase.from("books").select("id,title,author").eq("user_id", user.id).then(({ data }) => setMyBooks(data ?? []));
  }, [isOwner, user]);

  async function addBook(bookId: string) {
    const { error } = await supabase.from("shelf_books").insert({ shelf_id: id, book_id: bookId });
    if (error) return toast.error("Could not add. Already on the shelf?");
    toast.success("Added");
    setOpen(false);
    load();
  }

  async function removeBook(bookId: string) {
    const { error } = await supabase.from("shelf_books").delete().eq("shelf_id", id).eq("book_id", bookId);
    if (error) return toast.error("Could not remove");
    load();
  }

  if (!shelf) return <div className="min-h-screen bg-background"><Navbar /><p className="mt-12 text-center text-muted-foreground">…</p></div>;

  const onShelf = new Set(books.map((b) => b.id));
  const available = myBooks.filter((b) => !onShelf.has(b.id));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Link to="/shelves" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> All shelves
        </Link>
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-3xl">{shelf.emoji ?? "📚"}</div>
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold">{shelf.name}</h1>
            {shelf.description && <p className="mt-1 text-sm text-muted-foreground">{shelf.description}</p>}
          </div>
          {isOwner && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90"><Plus className="me-1 h-4 w-4" /> Add book</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add one of your books</DialogTitle></DialogHeader>
                {available.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No more of your books to add. List one first!</p>
                ) : (
                  <div className="max-h-80 space-y-2 overflow-y-auto">
                    {available.map((b) => (
                      <button key={b.id} onClick={() => addBook(b.id)}
                        className="flex w-full items-start justify-between gap-2 rounded-lg border p-3 text-left hover:bg-muted">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{b.title}</p>
                          <p className="truncate text-xs text-muted-foreground">{b.author}</p>
                        </div>
                        <Plus className="h-4 w-4 text-primary" />
                      </button>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>

        {books.length === 0 ? (
          <p className="mt-10 text-center text-sm text-muted-foreground">No books on this shelf yet.</p>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {books.map((b) => (
              <div key={b.id} className="relative">
                <BookCardItem book={b} here={coords} />
                {isOwner && (
                  <button onClick={() => removeBook(b.id)}
                    className="absolute end-2 top-2 z-10 rounded-full bg-background/90 p-1.5 text-destructive shadow-md hover:bg-destructive hover:text-white"
                    aria-label="Remove from shelf">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
